import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, MapPin, CreditCard, Bell, Users, Lock, Mail, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  const lastUpdated = "January 18, 2026";
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Privacy Policy</h1>
            <p className="text-sm text-gray-500">Last updated: {lastUpdated}</p>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm p-6 md:p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-pink-100 rounded-lg">
                <Shield className="h-6 w-6 text-pink-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Introduction</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Welcome to Tekstar ("we," "our," or "us"). We are committed to protecting your personal information 
              and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard 
              your information when you use our ride-sharing and package delivery mobile application and services 
              (collectively, the "Service").
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              By using Tekstar, you agree to the collection and use of information in accordance with this policy. 
              If you do not agree with the terms of this privacy policy, please do not access the application.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Information We Collect</h2>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">Personal Information</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We collect personal information that you voluntarily provide when registering for the Service:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Contact Information:</strong> Name, phone number, email address</li>
              <li><strong>Account Credentials:</strong> Password (encrypted)</li>
              <li><strong>Profile Information:</strong> Profile photo, preferred payment methods</li>
              <li><strong>Identity Verification:</strong> For drivers/bikers - driver's license, vehicle registration, insurance documents</li>
              <li><strong>Payment Information:</strong> Wallet balance, transaction history (we do not store full credit card numbers)</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">Location Information</h3>
            <div className="flex items-start gap-3 p-4 bg-yellow-50 rounded-lg">
              <MapPin className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Important:</strong> We collect precise location data when you use our Service. This includes:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                  <li>Pickup and drop-off locations</li>
                  <li>Real-time location during rides (for tracking and safety)</li>
                  <li>Background location for drivers when online/accepting rides</li>
                </ul>
              </div>
            </div>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">Automatically Collected Information</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers</li>
              <li><strong>Usage Data:</strong> App interactions, ride history, search queries</li>
              <li><strong>Log Data:</strong> IP address, access times, pages viewed, app crashes</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <CreditCard className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">How We Use Your Information</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Provide Services:</strong> Connect riders with drivers, process ride requests, enable package deliveries</li>
              <li><strong>Process Payments:</strong> Handle wallet transactions, process ride fares, manage refunds</li>
              <li><strong>Safety & Security:</strong> Verify identities, detect fraud, ensure rider and driver safety</li>
              <li><strong>Communication:</strong> Send ride confirmations, receipts, service updates, and promotional offers</li>
              <li><strong>Improve Services:</strong> Analyze usage patterns, fix bugs, develop new features</li>
              <li><strong>Customer Support:</strong> Respond to inquiries, resolve disputes, provide assistance</li>
              <li><strong>Legal Compliance:</strong> Comply with applicable laws and regulations in Zambia</li>
            </ul>
          </section>

          {/* Information Sharing */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Bell className="h-6 w-6 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Information Sharing</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">We may share your information with:</p>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Other Users</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>Riders see driver name, photo, vehicle details, and real-time location</li>
              <li>Drivers see rider name, pickup/drop-off locations, and phone number (masked for privacy)</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Service Providers</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>Payment processors (Stripe) for secure transactions</li>
              <li>Cloud hosting providers for data storage</li>
              <li>SMS/Email service providers (Twilio, Resend) for communications</li>
              <li>Map services (Google Maps) for navigation and routing</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Legal Requirements</h3>
            <p className="text-gray-700 leading-relaxed">
              We may disclose information if required by law, court order, or government regulation, 
              or to protect rights, property, or safety.
            </p>
          </section>

          {/* Data Security */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <Lock className="h-6 w-6 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Data Security</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              We implement appropriate technical and organizational security measures to protect your personal information:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Encryption of data in transit (HTTPS/TLS) and at rest</li>
              <li>Password hashing using industry-standard algorithms</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Access controls limiting employee access to personal data</li>
              <li>Secure cloud infrastructure with redundancy and backups</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              While we strive to protect your information, no method of transmission over the Internet is 100% secure. 
              We cannot guarantee absolute security.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Retention</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We retain your personal information for as long as necessary to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Provide our services to you</li>
              <li>Comply with legal obligations (tax records: 7 years)</li>
              <li>Resolve disputes and enforce agreements</li>
              <li>Maintain safety and security records</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Ride history and transaction records are kept for 3 years after your last activity. 
              You may request deletion of your account at any time.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and personal data</li>
              <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
              <li><strong>Withdraw Consent:</strong> Opt out of marketing communications</li>
              <li><strong>Location Settings:</strong> Control location sharing through your device settings</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              To exercise these rights, contact us at the details provided below.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Tekstar is not intended for users under 18 years of age. We do not knowingly collect personal 
              information from children. If you believe we have collected information from a child, 
              please contact us immediately.
            </p>
          </section>

          {/* Changes to Policy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by 
              posting the new Privacy Policy on this page and updating the "Last updated" date. 
              You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          {/* Contact Us */}
          <section className="bg-gray-50 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-gray-700">
                <Mail className="h-5 w-5 text-pink-600" />
                <span>privacy@tekstar.co.zm</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <Phone className="h-5 w-5 text-pink-600" />
                <span>+260 XXX XXX XXX</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <MapPin className="h-5 w-5 text-pink-600" />
                <span>Kasama, Northern Province, Zambia</span>
              </div>
            </div>
          </section>

          {/* Footer */}
          <div className="text-center pt-6 border-t">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Tekstar. All rights reserved.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
